import React, { useState } from 'react';

import Dock from '../../media/images/modules/loading-dock.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { useBuilding, day, printDate, nextId } from '../../state/building.js';

const SLOTS = ['7:00 AM – 9:00 AM', '9:00 AM – 12:00 PM', '1:00 PM – 4:00 PM', '4:00 PM – 6:00 PM'];
const STEPS = ['Scheduled', 'At dock', 'Complete'];
const STATUS_TONE = { 'Scheduled': 'info', 'At dock': 'warn', 'Complete': 'ok' };
const ACTION = { 'Scheduled': 'Arrived at dock', 'At dock': 'Mark complete' };

const SEED = [
    { id: 1, company: 'Restaurant Depot', unit: 'Lobby cafe', kind: 'Supplier', date: day(0), slot: SLOTS[0], elevator: false, status: 'Complete' },
    { id: 2, company: 'RH Delivery', unit: '1502', kind: 'Furniture delivery', date: day(0), slot: SLOTS[1], elevator: true, status: 'At dock' },
    { id: 3, company: 'Sub-Zero Wolf Service', unit: '2201', kind: 'Appliance install', date: day(0), slot: SLOTS[2], elevator: true, status: 'Scheduled' },
    { id: 4, company: 'Design Within Reach', unit: '907', kind: 'Furniture delivery', date: day(1), slot: SLOTS[1], elevator: true, status: 'Scheduled' }
];

/* How many days out a printed date is, so the schedule sorts by date. */
const daysOut = label => {
    for (let i = -7; i < 400; i++) if (day(i) === label) return i;
    return 400;
};

/* Deliveries and Loading Dock: every dock slot in one schedule, moves
   included, with bookings checked against what is already taken. */
function DeliveriesScreen() {
    const { moves } = useBuilding();
    const [bookings, setBookings] = useState(SEED);
    const [moveDock, setMoveDock] = useState({});
    const [flash, notify] = useFlash();

    /* Moves come from the shared store; their dock progress is kept here. */
    const moveRows = moves.filter(m => m.status !== 'Declined').map(m => ({
        id: `move-${m.id}`,
        moveId: m.id,
        company: `${m.kind}, Unit ${m.unit}`,
        unit: m.unit,
        kind: m.kind,
        date: m.date,
        slot: m.slot,
        elevator: true,
        approval: m.status,
        status: moveDock[m.id] || 'Scheduled'
    }));

    const all = [...bookings, ...moveRows];
    const order = r => SLOTS.indexOf(r.slot);
    const today = all.filter(r => r.date === day(0)).sort((a, b) => order(a) - order(b));
    const upcoming = all.filter(r => r.date !== day(0)).sort((a, b) => daysOut(a.date) - daysOut(b.date) || order(a) - order(b));

    const advance = row => {
        const next = STEPS[STEPS.indexOf(row.status) + 1];
        if (!next) return;
        if (row.moveId) setMoveDock(m => ({ ...m, [row.moveId]: next }));
        else setBookings(list => list.map(b => (b.id === row.id ? { ...b, status: next } : b)));
        notify(`${row.company} ${next === 'At dock' ? 'is at the dock' : 'is complete and the dock is clear'}.`);
    };

    const submit = event => {
        const values = readForm(event, ['company', 'unit']);
        if (!values) return notify('Add the company and the unit first.');

        const date = printDate(values.date);
        const taken = all.find(r => r.date === date && r.slot === values.slot);
        if (taken) return notify(`That slot is taken. ${taken.company} has the dock ${date}, ${values.slot}. Pick another slot.`);

        setBookings(list => [...list, {
            id: nextId(),
            company: values.company,
            unit: values.unit,
            kind: 'Delivery',
            date,
            slot: values.slot,
            elevator: Boolean(values.elevator),
            status: 'Scheduled'
        }]);
        notify(`Dock booked for ${values.company}, ${date}, ${values.slot}.${values.elevator ? ' Service elevator padded and reserved.' : ''}`);
    };

    const renderRow = row => (
        <li className="row" key={row.id}>
            <div className="row__main">
                <span className="row__title">{row.slot} · {row.company}</span>
                <span className="row__meta">
                    {row.kind}{row.moveId ? '' : ` · Unit ${row.unit}`} · {row.date}{row.elevator ? ' · service elevator' : ''}
                </span>
            </div>
            <div className="row__end">
                {row.approval === 'Pending' && <Chip tone="muted">Awaiting approval</Chip>}
                <Chip tone={STATUS_TONE[row.status]}>{row.status}</Chip>
                {ACTION[row.status] && (
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => advance(row)}>
                        {ACTION[row.status]}
                    </button>
                )}
            </div>
        </li>
    );

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Deliveries" title="Loading dock schedule" />

            <div className="banner">
                <img src={Dock} alt="" />
                <p className="banner__caption">Today's dock schedule: every move, delivery, and contractor slot in one view.</p>
            </div>

            <Kpis items={[
                { label: 'Slots today', value: `${today.length} of ${SLOTS.length}`, tone: 'info' },
                { label: 'At dock now', value: all.filter(r => r.status === 'At dock').length, tone: 'warn' },
                { label: 'Complete today', value: today.filter(r => r.status === 'Complete').length, tone: 'ok' },
                { label: 'Elevator bookings', value: all.filter(r => r.elevator && r.status !== 'Complete').length }
            ]} />

            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Book the dock</h4>
                    <div className="form">
                        <Field label="Company" wide>
                            <input name="company" className="form-control" placeholder="e.g. Best Buy Home Delivery" />
                        </Field>
                        <Field label="Unit">
                            <input name="unit" className="form-control" placeholder="e.g. 1204" />
                        </Field>
                        <Field label="Date">
                            <input type="date" name="date" className="form-control" />
                        </Field>
                        <Field label="Slot" wide>
                            <select name="slot" className="form-control" defaultValue={SLOTS[3]}>
                                {SLOTS.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                        <label className="field field--wide check">
                            <input type="checkbox" name="elevator" defaultChecked style={{ width: 'auto' }} />
                            <span>Service elevator needed (pads installed)</span>
                        </label>
                        <button type="submit" className="btn btn-primary">Book slot</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Today, {day(0)}</h4>
                    {today.length === 0 ? <Empty>The dock is open all day.</Empty> : <ul className="rows">{today.map(renderRow)}</ul>}

                    <h4>Coming up</h4>
                    {upcoming.length === 0 ? <Empty>Nothing booked yet.</Empty> : <ul className="rows">{upcoming.map(renderRow)}</ul>}
                </div>
            </div>
        </div>
    );
}

export default DeliveriesScreen;
